import { TableCell, TableRow } from "@mui/material";
import { Link } from "react-router-dom";
import { AccountPlanStatus, AppRoutes } from "../../../types/enums";
import { CellBody } from "./CellBody";
import { memo, useCallback, useMemo } from "react";
import { AccountListItem } from "../../../types";
import { ColumnList } from "./columnList";
import { CustomCheckbox, ReuseTooltip } from "../../ui";
import { CompanyMenu } from "../../features/CompanyMenu";
import { ProgressBar } from "../../ui";
import { EngagementScoreCell } from "./EngagementScoreCell";
import {
  useAppSelector,
  useSelectUpdatedCompany,
  useStateWithSearchParams,
  useUpdateAccountData,
} from "../../../hooks";
import { QUERY_SEPARATOR } from "../../../constant";
import { selectPermissions } from "../../../features/permissionsSlice";

type SearchParams = {
  selected?: string;
};

type Props = {
  row: AccountListItem;
  columns: ColumnList[];
  campaign_id: string;
  total_companies: number;
};

export const AccountListRow = memo(
  ({ row, columns, campaign_id, total_companies }: Props) => {
    const { isEditor } = useAppSelector((state) => state.company.userRoles);
    const { isAllowedEditLaunchAccountAnalysis } =
      useAppSelector(selectPermissions);

    const updatedData = useUpdateAccountData({ data: row });

    const { searchDict, setSearchValue } =
      useStateWithSearchParams<SearchParams>({
        selected: undefined,
      });

    const removeSelected = useCallback(
      (id: string) => {
        const selectedList = searchDict.selected?.split(QUERY_SEPARATOR);

        if (selectedList?.includes(id))
          setSearchValue(
            "selected",
            selectedList.filter((el) => el !== id).join(QUERY_SEPARATOR),
          );
      },
      [searchDict.selected, setSearchValue],
    );

    const checkBoxHandler = useCallback(
      (id: string) => {
        const selectedList = searchDict.selected?.split(QUERY_SEPARATOR);
        if (selectedList?.includes(id))
          setSearchValue(
            "selected",
            selectedList.filter((el) => el !== id).join(QUERY_SEPARATOR),
          );
        else {
          selectedList?.push(id);
          setSearchValue("selected", selectedList?.join(QUERY_SEPARATOR) || id);
        }
      },
      [searchDict.selected, setSearchValue],
    );

    const mapper = useMemo(() => {
      return {
        checkbox: (row: AccountListItem) => {
          const updatedCompany = useSelectUpdatedCompany(row.id);

          const selectedList = searchDict.selected?.split(QUERY_SEPARATOR);

          return (
            <ReuseTooltip
              text={
                !row.total_members &&
                "Contacts must be provided in order to initiate the analysis."
              }
              hideChildren={false}
              maxWidth={"200px"}
            >
              <CustomCheckbox
                onClick={() => checkBoxHandler(row.id)}
                checked={selectedList?.includes(row.id) || false}
                disabled={
                  !row.can_be_analyzed ||
                  !!updatedCompany ||
                  !isEditor ||
                  !isAllowedEditLaunchAccountAnalysis
                }
              />
            </ReuseTooltip>
          );
        },
        menu: (row: AccountListItem) => {
          return (
            <CompanyMenu
              isEditor={isEditor}
              campaign_id={campaign_id}
              isDisabled={row.company === "--"}
              total_companies={total_companies}
              id={row.id}
              companyName={row.company}
              websiteUrl={row.website_url!}
              removeSelected={removeSelected}
            />
          );
        },
        penetration_score: (row: AccountListItem) => <ProgressBar data={row} />,
        total_engagement_score: (row: AccountListItem) => (
          <EngagementScoreCell row={row} />
        ),
        text: () => null,
      };
    }, [
      checkBoxHandler,
      updatedData,
      isEditor,
      isAllowedEditLaunchAccountAnalysis,
    ]);

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isFirefox = /firefox/i.test(navigator.userAgent);

    return (
      <TableRow>
        {columns.map((column, cellIndex) => (
          <TableCell
            key={`${column.label}-${"rowIndex"}-${cellIndex}`}
            sx={{
              width:
                cellIndex === 0 && (isSafari || isFirefox)
                  ? "48px"
                  : cellIndex === 6 &&
                      row.account_plan_status === AccountPlanStatus.in_progress
                    ? "200px"
                    : column.maxWidth,
              height: "56px",
              paddingTop: 0,
              paddingBottom: 0,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0.3px",
              color: "#1F2932",
              textAlign: column.textAlign,

              backgroundColor: !row.is_new
                ? cellIndex === 0
                  ? "#E1E6EF"
                  : "transparent"
                : cellIndex === 0
                  ? "#D8DDE6"
                  : "#F7F9FD",
              borderBottom: "1px solid #0000001F",
              paddingRight: 0,
              paddingLeft: `${
                [
                  "checkbox",
                  "total_engagement_score",
                  "penetration_score",
                ].includes(column.type)
                  ? "0"
                  : "8px"
              }`,
            }}
            style={{
              ...("data_source" === column.id && {
                paddingLeft: "16px",
              }),
            }}
          >
            {mapper[column.type](updatedData) || (
              <>
                {cellIndex === 1 ? (
                  <Link
                    onClick={(e) =>
                      updatedData.company === "--" && e.preventDefault()
                    }
                    to={`${AppRoutes.ACCOUNT}/${campaign_id}/${updatedData.id}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <CellBody
                      row={updatedData}
                      cellIndex={cellIndex}
                      column={column}
                    />
                  </Link>
                ) : (
                  <div>
                    <CellBody
                      row={updatedData}
                      cellIndex={cellIndex}
                      column={column}
                    />
                  </div>
                )}
              </>
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  },
);
